import React from 'react'
import { Link } from 'react-router-dom'
import {
  TrendingUp,
  Users,
  FileText,
  DollarSign,
  ArrowUpRight,
  ArrowRight,
  Plus,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts'
import { mockCampaigns, mockAnalytics, statusColors, formatCurrency, formatNumber } from '../../data/mockData'

const stats = [
  {
    name: 'Total Reach',
    value: '2.4M',
    change: '+12.5%',
    changeType: 'positive',
    icon: Eye,
    color: 'bg-blue-500',
  },
  {
    name: 'Active Campaigns',
    value: '2',
    change: '+1 this month',
    changeType: 'positive',
    icon: FileText,
    color: 'bg-purple-500',
  },
  {
    name: 'Creators Engaged',
    value: '13',
    change: '+3 new',
    changeType: 'positive',
    icon: Users,
    color: 'bg-teal-500',
  },
  {
    name: 'Total Spend',
    value: '$75K',
    change: '3.2x ROI',
    changeType: 'positive',
    icon: DollarSign,
    color: 'bg-green-500',
  },
]

const recentActivity = [
  {
    id: 1,
    type: 'content_approved',
    message: 'Dr. Betsy Grunch\'s TikTok video was approved',
    campaign: 'Spring Wellness Launch',
    time: '2 hours ago',
    icon: CheckCircle,
    iconColor: 'text-green-500',
  },
  {
    id: 2,
    type: 'draft_submitted',
    message: 'New draft submitted by Dr. Sarah Chen',
    campaign: 'Spring Wellness Launch',
    time: '5 hours ago',
    icon: FileText,
    iconColor: 'text-blue-500',
  },
  {
    id: 3,
    type: 'creator_matched',
    message: '3 creators matched to your campaign',
    campaign: 'New Product Announcement',
    time: '1 day ago',
    icon: Users,
    iconColor: 'text-purple-500',
  },
  {
    id: 4,
    type: 'deadline_reminder',
    message: 'Content deadline approaching in 3 days',
    campaign: 'Patient Education Series',
    time: '1 day ago',
    icon: Clock,
    iconColor: 'text-orange-500',
  },
]

function BrandDashboard() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">Welcome back! Here's what's happening with your campaigns.</p>
        </div>
        <Link to="/brand/campaigns/new" className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="stat-card">
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <span className={`text-sm font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.name}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Campaign Performance</h2>
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
              <option>Last 30 days</option>
              <option>Last 7 days</option>
              <option>Last 90 days</option>
            </select>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={mockAnalytics.performanceOverTime}>
                <defs>
                  <linearGradient id="colorReach" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(val) => formatNumber(val)} />
                <Tooltip
                  formatter={(value) => [formatNumber(value), '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Area
                  type="monotone"
                  dataKey="reach"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorReach)"
                  name="Reach"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`mt-0.5 ${activity.iconColor}`}>
                  <activity.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {activity.campaign} • {activity.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium">
            View all activity
          </button>
        </div>
      </div>

      {/* Active Campaigns */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Active Campaigns</h2>
          <Link to="/brand/campaigns" className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center">
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Creators</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Progress</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Reach</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mockCampaigns.slice(0, 3).map((campaign) => {
                const status = statusColors[campaign.status] || statusColors.draft
                const progress = campaign.deliverablesCount > 0
                  ? Math.round((campaign.completedDeliverables / campaign.deliverablesCount) * 100)
                  : 0

                return (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{campaign.name}</p>
                        <p className="text-sm text-gray-500">{formatCurrency(campaign.budget)} budget</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-600">{campaign.creatorsCount}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-[100px]">
                          <div
                            className="h-full bg-hf-teal-500 rounded-full"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500">{progress}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {campaign.reach > 0 ? formatNumber(campaign.reach) : '-'}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <Link
                        to={`/brand/campaigns/${campaign.id}`}
                        className="text-hf-teal-600 hover:text-hf-teal-700 text-sm font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link
          to="/brand/campaigns/new"
          className="card hover:border-hf-teal-200 hover:shadow-md transition-all group cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-hf-teal-100 rounded-xl flex items-center justify-center group-hover:bg-hf-teal-200 transition-colors">
              <Plus className="w-6 h-6 text-hf-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Create Campaign</p>
              <p className="text-sm text-gray-500">Start a new brief</p>
            </div>
          </div>
        </Link>
        <Link
          to="/brand/creators"
          className="card hover:border-teal-200 hover:shadow-md transition-all group cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center group-hover:bg-teal-200 transition-colors">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Discover Creators</p>
              <p className="text-sm text-gray-500">Browse vetted creators</p>
            </div>
          </div>
        </Link>
        <Link
          to="/brand/analytics"
          className="card hover:border-purple-200 hover:shadow-md transition-all group cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">View Analytics</p>
              <p className="text-sm text-gray-500">Track performance</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default BrandDashboard
